"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import { MoveRight, ArrowLeft, RotateCcw, Check } from "lucide-react";
import { z } from "zod/v4";
import { cn } from "@/lib/utils";
import { Container } from "@/components/layout/container";
import { Button } from "@/components/ui/button";
import { ProgressBar } from "@/components/ui/progress-bar";
import { SubsidyBadge } from "@/components/ui/subsidy-badge";
import { Contour } from "@/components/marketing/contour";

/* -------------------------------------------------------------------------- */
/*  Types & data                                                              */
/* -------------------------------------------------------------------------- */

interface Question {
  id: number;
  question: string;
  options: { label: string; points: number }[];
}

interface LearningPath {
  title: string;
  description: string;
  href: string;
  level: string;
}

const QUESTIONS: Question[] = [
  {
    id: 1,
    question: "How would you describe your current understanding of artificial intelligence?",
    options: [
      { label: "I've heard the term but I'm not really sure what AI is", points: 0 },
      { label: "I know the basics. AI can learn from data and make predictions", points: 1 },
      { label: "I understand concepts like machine learning, neural networks, and training data", points: 2 },
      { label: "I've built or fine-tuned AI models myself", points: 3 },
    ],
  },
  {
    id: 2,
    question: "Have you used AI tools like chatbots or image generators?",
    options: [
      { label: "No, I haven't tried any AI tools yet", points: 0 },
      { label: "I've tried one or two tools casually", points: 1 },
      { label: "I use AI tools regularly for work or personal projects", points: 2 },
      { label: "I use advanced features like custom instructions, API access, or prompt chaining", points: 3 },
    ],
  },
  {
    id: 3,
    question: "What best describes your experience with writing prompts for AI?",
    options: [
      { label: "I'm not sure what a \"prompt\" is in this context", points: 0 },
      { label: "I type basic questions and see what comes back", points: 1 },
      { label: "I structure my prompts with context, constraints, and examples", points: 2 },
      { label: "I design multi-step prompt workflows and evaluate outputs systematically", points: 3 },
    ],
  },
  {
    id: 4,
    question: "How comfortable are you with using technology to solve problems at work?",
    options: [
      { label: "I stick to email and basic office software", points: 0 },
      { label: "I'm comfortable with most apps and learn new tools when needed", points: 1 },
      { label: "I automate tasks, use APIs, or write scripts occasionally", points: 2 },
      { label: "I write code regularly and build custom solutions", points: 3 },
    ],
  },
  {
    id: 5,
    question: "What's your main goal for learning about AI?",
    options: [
      { label: "Just curious. I want to understand what all the fuss is about", points: 0 },
      { label: "I want to be more productive at my current job", points: 1 },
      { label: "I'm looking to lead AI adoption in my team or organisation", points: 2 },
      { label: "I want to build AI-powered products or switch into an AI career", points: 3 },
    ],
  },
];

const PATHS: Record<string, LearningPath> = {
  beginner: {
    title: "AI Foundations",
    description:
      "Start with the essentials. You'll learn what AI is, how it works, and how to use AI assistants confidently. Perfect for complete beginners.",
    href: "/paths",
    level: "Beginner",
  },
  intermediate: {
    title: "AI Power User",
    description:
      "You've got the basics down. Time to master prompt engineering, explore advanced AI tools, and learn how to apply AI effectively in your work.",
    href: "/paths",
    level: "Intermediate",
  },
  advanced: {
    title: "AI Builder",
    description:
      "You're ready for the deep end. Learn to build AI-powered applications, work with APIs, fine-tune models, and lead AI strategy in your organisation.",
    href: "/paths",
    level: "Advanced",
  },
};

function getPath(score: number): LearningPath {
  if (score <= 5) return PATHS.beginner;
  if (score <= 10) return PATHS.intermediate;
  return PATHS.advanced;
}

const emailSchema = z.object({
  email: z.email("Please enter a valid email address"),
});

type EmailStatus = "idle" | "loading" | "success" | "error";

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export default function QuizPage() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showResults, setShowResults] = useState(false);

  /* Email capture on results */
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] = useState<EmailStatus>("idle");
  const [submittedEmail, setSubmittedEmail] = useState("");

  const totalQuestions = QUESTIONS.length;
  const question = QUESTIONS[currentQuestion];
  const totalScore = answers.reduce((sum, pts) => sum + pts, 0);
  const path = getPath(totalScore);

  /* ---- Navigation ---- */

  const handleNext = useCallback(() => {
    if (selectedOption === null) return;

    const newAnswers = [...answers, selectedOption];
    setAnswers(newAnswers);
    setSelectedOption(null);

    if (currentQuestion + 1 >= totalQuestions) {
      setShowResults(true);
    } else {
      setCurrentQuestion((prev) => prev + 1);
    }
  }, [selectedOption, answers, currentQuestion, totalQuestions]);

  const handleBack = useCallback(() => {
    if (currentQuestion === 0) return;
    const prevAnswers = answers.slice(0, -1);
    setAnswers(prevAnswers);
    setSelectedOption(null);
    setCurrentQuestion((prev) => prev - 1);
  }, [currentQuestion, answers]);

  const handleRestart = useCallback(() => {
    setCurrentQuestion(0);
    setAnswers([]);
    setSelectedOption(null);
    setShowResults(false);
    setEmail("");
    setEmailError(null);
    setEmailStatus("idle");
    setSubmittedEmail("");
  }, []);

  /* ---- Email ---- */

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setEmailError(null);

      const result = emailSchema.safeParse({ email });
      if (!result.success) {
        setEmailError(result.error.issues[0].message);
        return;
      }

      const trimmed = result.data.email.trim();
      setEmailStatus("loading");

      try {
        const res = await fetch("/api/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: trimmed, source: "quiz" }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          message?: string;
          error?: string;
        };
        if (!res.ok) {
          throw new Error(
            data.error ?? "Something went wrong. Please try again.",
          );
        }
        setSubmittedEmail(trimmed);
        setEmailStatus("success");
      } catch (err) {
        setEmailStatus("error");
        setEmailError(
          err instanceof Error
            ? err.message
            : "Something went wrong. Please try again.",
        );
      }
    },
    [email],
  );

  /* ---------------------------------------------------------------------- */
  /*  Results screen                                                        */
  /* ---------------------------------------------------------------------- */

  if (showResults) {
    const maxScore = totalQuestions * 3;
    const percentage = Math.round((totalScore / maxScore) * 100);

    return (
      <section className="relative overflow-hidden">
        <Contour
          variant="rings"
          className="pointer-events-none absolute -right-32 -top-40 size-[640px] text-primary"
        />
        <Container narrow className="relative py-14 lg:py-20">
          {/* Heading */}
          <span className="eyebrow">
            AI skill quiz
            <span className="rule" aria-hidden="true" />
            Your result
          </span>
          <h1 className="mt-5 text-balance text-4xl font-semibold sm:text-5xl">
            You scored{" "}
            <span className="text-secondary">
              {totalScore} of {maxScore}
            </span>
          </h1>

          {/* Score bar */}
          <div className="mt-8">
            <ProgressBar
              value={totalScore}
              max={maxScore}
              label="Quiz score"
              hideText
            />
            <p className="mt-2 text-sm text-mid">
              {percentage}%, {path.level} level
            </p>
          </div>

          {/* Recommended path */}
          <div className="mt-10 rounded-md border border-line bg-paper p-6 sm:p-8">
            <span className="eyebrow">Recommended for you</span>
            <h2 className="mt-3 font-heading text-2xl font-semibold text-dark">
              {path.title}
            </h2>
            <p className="mt-3 text-base leading-relaxed text-mid">
              {path.description}
            </p>

            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button asChild>
                <Link href={path.href}>
                  Start this path
                  <MoveRight className="size-5" aria-hidden="true" />
                </Link>
              </Button>
              <SubsidyBadge />
            </div>
          </div>

          {/* Email capture */}
          <div className="mt-8 rounded-md border border-line bg-light-bg p-6 sm:p-8">
            {emailStatus === "success" ? (
              <div className="flex items-start gap-3">
                <Check
                  className="mt-0.5 size-6 shrink-0 text-secondary"
                  aria-hidden="true"
                />
                <div>
                  <p className="font-heading text-xl font-semibold text-dark">
                    Check your inbox
                  </p>
                  <p
                    role="status"
                    className="mt-1 text-sm leading-relaxed text-mid"
                  >
                    We&rsquo;ve sent your result and a getting-started plan to{" "}
                    <strong className="text-dark">{submittedEmail}</strong>.
                    Confirm the subscription to receive it.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <span className="eyebrow">Save your result</span>
                <h3 className="mt-3 font-heading text-xl font-semibold text-dark">
                  Get your plan by email
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-mid">
                  We&rsquo;ll send your score, your recommended path, and a free
                  getting-started guide. No spam. Leave whenever you like.
                </p>

                <form
                  onSubmit={handleEmailSubmit}
                  noValidate
                  className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-start"
                >
                  <div className="flex flex-1 flex-col gap-1">
                    <label htmlFor="quiz-email" className="sr-only">
                      Email address
                    </label>
                    <input
                      id="quiz-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError(null);
                        if (emailStatus === "error") setEmailStatus("idle");
                      }}
                      placeholder="you@example.co.uk"
                      aria-invalid={emailError ? true : undefined}
                      aria-describedby={
                        emailError ? "quiz-email-error" : undefined
                      }
                      className={cn(
                        "min-h-[48px] w-full rounded-md border bg-paper px-4 py-2.5",
                        "text-base text-dark placeholder:text-mid/60",
                        "transition-colors duration-150",
                        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
                        emailError
                          ? "border-error focus-visible:outline-error"
                          : "border-line hover:border-mid/50",
                      )}
                    />
                    {emailError && (
                      <p
                        id="quiz-email-error"
                        role="alert"
                        className="text-sm font-medium text-error"
                      >
                        {emailError}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    variant="secondary"
                    size="lg"
                    className="shrink-0"
                    disabled={emailStatus === "loading"}
                  >
                    {emailStatus === "loading"
                      ? "Sending…"
                      : "Send my result"}
                    {emailStatus !== "loading" && (
                      <MoveRight className="size-4" aria-hidden="true" />
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>

          {/* Restart */}
          <div className="mt-8">
            <button
              type="button"
              onClick={handleRestart}
              className={cn(
                "inline-flex items-center gap-2 rounded-sm text-sm font-medium text-mid underline underline-offset-4",
                "transition-colors hover:text-dark",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
              )}
            >
              <RotateCcw className="size-4" aria-hidden="true" />
              Take the quiz again
            </button>
          </div>
        </Container>
      </section>
    );
  }

  /* ---------------------------------------------------------------------- */
  /*  Question screen                                                       */
  /* ---------------------------------------------------------------------- */

  return (
    <section className="relative overflow-hidden">
      <Contour
        variant="rings"
        className="pointer-events-none absolute -right-32 -top-40 size-[640px] text-primary"
      />
      <Container narrow className="relative py-14 lg:py-20">
        {/* Header */}
        <span className="eyebrow">
          AI skill quiz
          <span className="rule" aria-hidden="true" />
          Five questions
        </span>
        <h1 className="mt-5 text-balance text-4xl font-semibold sm:text-5xl">
          Find your AI level
        </h1>
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-mid">
          Answer {totalQuestions} quick questions and we&rsquo;ll point you to
          the learning path that fits your experience.
        </p>

        {/* Progress */}
        <div className="mt-10">
          <ProgressBar
            value={currentQuestion}
            max={totalQuestions}
            label="Quiz progress"
            hideText
          />
          <p className="mt-2 text-sm font-medium text-mid">
            Question {currentQuestion + 1} of {totalQuestions}
          </p>
        </div>

        {/* Question */}
        <fieldset className="mt-8">
          <legend className="font-heading text-2xl font-semibold text-dark">
            {question.question}
          </legend>

          <div className="mt-5 flex flex-col gap-3">
            {question.options.map((option, index) => {
              const isSelected = selectedOption === option.points;
              const optionId = `q${question.id}-option-${index}`;

              return (
                <label
                  key={index}
                  htmlFor={optionId}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-md border p-4",
                    "transition-colors duration-150",
                    "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-secondary",
                    isSelected
                      ? "border-secondary bg-secondary/[0.08]"
                      : "border-line bg-paper hover:border-secondary/60 hover:bg-secondary/[0.04]",
                  )}
                >
                  <input
                    id={optionId}
                    type="radio"
                    name={`question-${question.id}`}
                    value={option.points}
                    checked={isSelected}
                    onChange={() => setSelectedOption(option.points)}
                    className="mt-1 size-4 shrink-0 accent-primary"
                  />
                  <span className="text-base leading-relaxed text-dark">
                    {option.label}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between border-t border-line pt-6">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentQuestion === 0}
            className={cn(
              "inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-4 py-2",
              "text-sm font-medium text-mid",
              "transition-colors hover:bg-primary/[0.06] hover:text-dark",
              "disabled:pointer-events-none disabled:opacity-40",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
            )}
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back
          </button>

          <Button onClick={handleNext} disabled={selectedOption === null}>
            {currentQuestion + 1 === totalQuestions ? "See result" : "Next"}
            <MoveRight className="size-5" aria-hidden="true" />
          </Button>
        </div>
      </Container>
    </section>
  );
}
